"use client";

import type { RouterOutput } from "@lattice/backend/trpc";
import { cn } from "@lattice/frontend/lib/utils/ui";
import { formatDistanceToNow } from "date-fns";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import React from "react";
import NewsImageThumbnail from "./news-image-thumbnail";
import {
  cardGridGap,
  bodyTextClass,
  compactCardPadding,
} from "@lattice/frontend/lib/styles/common";

export default function StockNewsSectionContent({
  news,
}: {
  news: RouterOutput["stock"]["get_news"];
}) {
  const [showAll, setShowAll] = React.useState(false);
  const displayedNews = showAll ? news : news.slice(0, 6);

  return (
    <>
      <div className={cn("grid grid-cols-1 lg:grid-cols-2", cardGridGap)}>
        {news.length ? (
          displayedNews.map((news) => (
            <div
              key={news.id}
              className={cn(
                "bg-grey-7 rounded-lg flex items-center gap-4 border border-[#131314]",
                compactCardPadding,
              )}
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-secondary/30 rounded-sm flex items-center justify-center">
                {news.images?.[0]?.url ? (
                  <NewsImageThumbnail
                    src={news.images[0].url}
                    alt={news.headline + " image"}
                  />
                ) : (
                  <img
                    src="/whale-icon.png"
                    alt="Lattice Logo"
                    className="w-full h-full object-contain rounded-sm p-2"
                  />
                )}
              </div>
              <div className="flex flex-col justify-center space-y-1.5 flex-1 min-w-0">
                {news.url ? (
                  <Link
                    prefetch={false}
                    href={news.url}
                    className={cn(
                      "hover:underline line-clamp-2",
                      bodyTextClass,
                    )}
                    target="_blank"
                  >
                    {news.headline}
                  </Link>
                ) : (
                  <b className={cn("line-clamp-2", bodyTextClass)}>
                    {news.headline}
                  </b>
                )}
                <p className="text-xs font-light text-muted-foreground/80 flex items-center gap-1.5">
                  <span>
                    {news.source.startsWith("Finviz: ")
                      ? news.source.substring(8)
                      : news.source}
                  </span>
                  <span className="text-muted-foreground/50">•</span>
                  <span>
                    {formatDistanceToNow(news.updated_at).replace("about", "")}{" "}
                    ago
                  </span>
                </p>
              </div>
            </div>
          ))
        ) : (
          <p className="text-muted-foreground">No news available</p>
        )}
      </div>

      {/* See More / See Less Button */}
      {news.length > 6 && (
        <div className="flex justify-end mt-4 col-span-2">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <span>{showAll ? "See Less" : "See More"}</span>
            <ChevronRight
              className={cn(
                "w-4 h-4 transition-transform",
                showAll ? "rotate-90" : "",
              )}
            />
          </button>
        </div>
      )}
    </>
  );
}
